import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Eye, Brain, Key, BarChart3, Layers } from 'lucide-react';

const features = [
  {
    icon: ShieldCheck,
    title: "Advanced Encryption",
    description: "Military-grade 256-bit encryption for healthcare AI training data with HIPAA compliance."
  },
  {
    icon: Eye,
    title: "Real-Time Monitoring",
    description: "99.8% accuracy in real-time threat identification specific to healthcare AI systems."
  },
  {
    icon: Brain,
    title: "Proactive Prevention",
    description: "Predictive security using behavioral analysis, preventing attacks before they occur."
  },
  {
    icon: Key,
    title: "Access Control",
    description: "Time-based and role-based restrictions with attribute-based access control."
  },
  {
    icon: BarChart3,
    title: "Compliance Ready",
    description: "Comprehensive audit trails for FDA, HIPAA, and HITECH compliance reporting."
  },
  {
    icon: Layers,
    title: "End-to-End Protection",
    description: "Complete AI lifecycle coverage from data ingestion to clinical deployment."
  }
];

export default function SolutionSectionV2() {
  return (
    <section id="features" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Background effect */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(45,212,191,0.1),transparent_70%)]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-block px-4 py-2 rounded-full border border-[#2dd4bf]/30 bg-[#2dd4bf]/10 text-[#2dd4bf] text-sm font-medium mb-6">
            Comprehensive Platform
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Built for <span className="bg-gradient-to-r from-[#2dd4bf] to-blue-400 bg-clip-text text-transparent">Healthcare AI</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Complete protection across the entire AI lifecycle with healthcare-specific customization
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#2dd4bf]/10 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 group-hover:border-[#2dd4bf]/30 transition-all duration-300 h-full">
                <div className="w-14 h-14 rounded-2xl bg-[#2dd4bf]/10 flex items-center justify-center border border-[#2dd4bf]/20 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-[#2dd4bf]" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}